import React, { useEffect, useState } from "react";
import ControlLayout from "../components/ControlLayout";
import { api, formatDetail } from "../lib/api";
import { Lock, RotateCcw, Plus, Copy, Check } from "lucide-react";

export default function ControlDevices() {
  const [devices, setDevices] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newDevice, setNewDevice] = useState(null);
  const [name, setName] = useState("");
  const [outletId, setOutletId] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const refresh = async () => {
    const [d, o] = await Promise.all([api.get("/devices"), api.get("/outlets")]);
    setDevices(d.data);
    setOutlets(o.data);
    if (!outletId && o.data.length) setOutletId(o.data[0].id);
  };

  useEffect(() => { refresh(); }, []);

  const create = async () => {
    setError("");
    try {
      const r = await api.post("/devices", { device_name: name, outlet_id: outletId });
      setNewDevice(r.data);
      setName("");
      refresh();
    } catch (e) { setError(formatDetail(e.response?.data?.detail)); }
  };

  const revoke = async (id) => {
    await api.post(`/devices/${id}/revoke`);
    refresh();
  };
  const reactivate = async (id) => {
    await api.post(`/devices/${id}/reactivate`);
    refresh();
  };

  return (
    <ControlLayout
      title="Devices"
      subtitle="Registered tablets / kiosks · token issuance · revocation"
      actions={
        <button
          data-testid="btn-register-device"
          onClick={() => { setShowCreate(true); setNewDevice(null); }}
          className="h-11 px-4 bg-black text-white font-mono text-xs uppercase tracking-wider inline-flex items-center gap-2"
        >
          <Plus size={14} /> Register device
        </button>
      }
    >
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8">
          <div className="panel">
            <div className="panel-header"><span className="mono-label">Devices · {devices.length}</span></div>
            <table className="w-full text-sm" data-testid="devices-table">
              <thead className="bg-[#FAFAFA] border-b border-neutral-200">
                <tr className="text-left">
                  <Th>Device</Th><Th>Outlet</Th><Th>Status</Th><Th>Last seen</Th><Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`device-row-${d.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{d.device_name}</div>
                      <div className="mono-label" style={{ fontSize: 10 }}>{d.id}</div>
                    </td>
                    <td className="px-4 py-3 mono-value text-xs">{d.outlet_id}</td>
                    <td className="px-4 py-3">
                      <StatusPill status={d.status} />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{d.last_seen_at_iso ? new Date(d.last_seen_at_iso).toLocaleString("en-GB") : "—"}</td>
                    <td className="px-4 py-3">
                      {d.status === "active" ? (
                        <button data-testid={`revoke-${d.id}`} onClick={() => revoke(d.id)} className="h-9 px-3 border border-red-500 text-red-700 font-mono text-xs uppercase tracking-wider inline-flex items-center gap-2">
                          <Lock size={12} /> Revoke
                        </button>
                      ) : (
                        <button data-testid={`reactivate-${d.id}`} onClick={() => reactivate(d.id)} className="h-9 px-3 border border-neutral-300 font-mono text-xs uppercase tracking-wider inline-flex items-center gap-2">
                          <RotateCcw size={12} /> Reactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {devices.length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center mono-label">No devices yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-4">
          {showCreate && (
            <div className="panel">
              <div className="panel-header">
                <span className="mono-label">Register new device</span>
                <button onClick={() => { setShowCreate(false); setNewDevice(null); }} className="mono-label hover:underline">Close</button>
              </div>
              <div className="p-4">
                {!newDevice ? (
                  <>
                    <label className="mono-label">Device name</label>
                    <input
                      data-testid="device-name-input"
                      value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="BENO Assagao Kitchen Tablet"
                      className="w-full mt-2 h-12 px-3 border border-neutral-300 bg-white font-mono focus:outline-none focus:border-black"
                    />
                    <label className="mono-label mt-4 block">Outlet</label>
                    <select
                      data-testid="device-outlet-input"
                      value={outletId} onChange={(e) => setOutletId(e.target.value)}
                      className="w-full mt-2 h-12 px-3 border border-neutral-300 bg-white font-mono focus:outline-none focus:border-black"
                    >
                      {outlets.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                    {error && <div className="text-red-700 font-mono text-sm mt-2">{error}</div>}
                    <button
                      data-testid="btn-submit-register-device"
                      onClick={create}
                      disabled={!name}
                      className="mt-4 w-full h-12 bg-black text-white font-mono text-sm uppercase tracking-wider disabled:opacity-50"
                    >
                      Issue device token
                    </button>
                  </>
                ) : (
                  <>
                    <div className="mono-label">Device registered</div>
                    <div className="mt-2 font-semibold">{newDevice.device.device_name}</div>
                    <div className="mono-label mt-4">Device token (copy once)</div>
                    <div className="mt-2 bg-black text-white p-3 font-mono text-[11px] break-all" data-testid="new-device-token">
                      {newDevice.device_token}
                    </div>
                    <button
                      onClick={() => { navigator.clipboard.writeText(newDevice.device_token); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                      className="mt-2 h-10 w-full border border-neutral-300 font-mono text-xs uppercase tracking-wider inline-flex items-center justify-center gap-2"
                    >
                      {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy token"}
                    </button>
                    <p className="mt-3 text-xs text-neutral-600">Store this securely. The People Engine retains a hashed reference; this is the only time the token is shown.</p>
                  </>
                )}
              </div>
            </div>
          )}
          <div className="panel mt-6">
            <div className="panel-header"><span className="mono-label">Rules</span></div>
            <ul className="p-4 space-y-2 text-sm text-neutral-700">
              <li>· One device belongs to one outlet.</li>
              <li>· Device token is scoped to <code className="font-mono">attendance_scan</code> only.</li>
              <li>· Revoked devices are locked instantly.</li>
              <li>· Lost devices must be revoked before day close.</li>
            </ul>
          </div>
        </div>
      </div>
    </ControlLayout>
  );
}

const Th = ({ children }) => <th className="px-4 py-2 mono-label text-left" style={{ fontSize: 10 }}>{children}</th>;

function StatusPill({ status }) {
  const map = {
    active: "border-[#22C55E] bg-[#DCFCE7] text-[#166534]",
    revoked: "border-[#EF4444] bg-[#FEE2E2] text-[#991B1B]",
  };
  return (
    <span className={`px-2 py-1 border font-mono text-[10px] uppercase tracking-wider ${map[status] || "border-neutral-300 bg-neutral-100"}`}>
      {status}
    </span>
  );
}
